import { FC, useState } from 'react'

import {
  Card,
  Cluster,
  Icon,
  IconButton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Typography,
  Upload,
  useToaster,
} from '@forto-dev/phoenix-ui-core'

import { useAuth } from '@/features/auth'
import { useEntityDocumentCreate } from '@/features/documents'
import { DocumentUploadActions, RequiredActionV2 } from '@/features/workbench'
import { useLocalisation } from '@/utils/localisation'

type UploadFileProps = {
  onSelectDocument: (file: File | null) => void
}

const UploadFile: FC<UploadFileProps> = ({ onSelectDocument }) => {
  const { translate } = useLocalisation()

  return (
    <Upload
      key="upload-form-"
      label={translate('documents.upload.label')}
      description={translate('documents.upload.description')}
      accept="image/jpeg,image/png,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      onChange={(files) => {
        onSelectDocument(files?.[0] ?? null)
      }}
    />
  )
}

type UploadedFileProps = {
  selectedFile: File
  onSelectDocument: (file: File | null) => void
}

const UploadedFile: FC<UploadedFileProps> = ({ selectedFile, onSelectDocument }) => {
  const { translate } = useLocalisation()

  return (
    <Card>
      <Cluster x="between">
        <Cluster gap={16} y="center">
          <Icon name="Document" />
          <Typography>{selectedFile.name}</Typography>
        </Cluster>
        <Cluster gap={16}>
          <Tooltip>
            <TooltipTrigger>
              <Upload accept="image/*,.pdf" onChange={(files) => onSelectDocument(files?.[0] ?? null)}>
                <IconButton
                  icon="Upload"
                  label={translate('required-actions.dialog.choose-another-doc')}
                  color="primary"
                />
              </Upload>
            </TooltipTrigger>
            <TooltipContent>{translate('required-actions.dialog.choose-another-doc')}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger>
              <IconButton
                icon="Bin"
                label={translate('required-actions.dialog.remove-doc')}
                color="primary"
                onClick={() => onSelectDocument(null)}
              />
            </TooltipTrigger>
            <TooltipContent>{translate('required-actions.dialog.remove-doc')}</TooltipContent>
          </Tooltip>
        </Cluster>
      </Cluster>
    </Card>
  )
}

// * Important: Do not edit unless the following roles are updated in BE.
const actionTypeToDocumentRoleMap: Record<DocumentUploadActions, string> = {
  // Shipment Documents
  completePackingList: 'packingList',
  provideCommercialInvoice: 'commercialInvoice',
  providePaymentProof: 'proofOfPayment',
  provideMSDS: 'materialSafetyDataSheet',
  provideIMODeclaration: 'imoDeclaration',
  // Company Documents
  provideCustomsWarrantBelgiumFiscal: 'fiscalRepresentationImportBelgium',
  provideCustomsWarrantBelgiumPOA: 'powerOfAttorneyImportBelgium',
  provideCustomsWarrantNetherlandsFiscal: 'fiscalRepresentationImportNetherlands',
  provideCustomsWarrantNetherlandsPOA: 'powerOfAttorneyImportNetherlands',
  provideCustomsWarrantGermanySeaPOA: 'powerOfAttorneyImportGermany',
  provideCustomsWarrantGermanyAirPOA: 'powerOfAttorneyImportGermany-AirOnly',
  provideCustomsWarrantSelfCustomsDeclaration: 'self-CustomsDeclarationGermany',
  provideCustomsWarrantBelgiumExportPOA: 'powerOfAttoneyExportBelgium',
  provideCustomsWarrantGermanyExportPOA: 'powerOfAttorney',
  provideCustomsWarrantGermanyAirExportPOA: 'powerOfAttorney',
  provideCustomsWarrantNetherlandsExportPOA: 'powerOfAttorneyExportNetherlands',
}

type ActionFileUploadProps = {
  action: RequiredActionV2
  setSubmitCallback: (cb: (() => Promise<void>) | undefined) => void
}

export const ActionFileUpload: FC<ActionFileUploadProps> = ({ action, setSubmitCallback }) => {
  const { translate } = useLocalisation()
  const { customer } = useAuth()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const toaster = useToaster()
  const createEntityDocumentMutation = useEntityDocumentCreate()

  const handleUploadDocument = async (uploadFile: File) => {
    const actionType = action.type as DocumentUploadActions
    const documentTypeCode = actionTypeToDocumentRoleMap[actionType]
    if (documentTypeCode) {
      try {
        if (action.category === 'documentUpload') {
          // Shipment document upload
          await createEntityDocumentMutation.mutateAsync({
            entityId: action.entityId,
            data: {
              file: uploadFile,
              state: 'published',
              mimeType: uploadFile.type,
              customerID: customer?._id ?? '',
              source: 'customer',
              documentTypeCode,
            },
          })
        } else {
          // company document upload
          await createEntityDocumentMutation.mutateAsync({
            entityId: customer?._id ?? '',
            data: {
              file: uploadFile,
              state: 'published',
              mimeType: uploadFile.type,
              customerID: customer?._id ?? '',
              source: 'customer',
              documentTypeCode,
            },
          })
        }
        toaster({
          title: translate('required-actions.dialog.documentUploadSuccess', {
            document: translate(`required-actions.document.${actionType}`),
          }),
          variant: 'positive',
        })
      } catch (error) {
        toaster({
          title: translate('required-actions.dialog.documentUploadError'),
          description: error instanceof Error ? error.message : translate('errorDescription'),
          variant: 'negative',
        })
      }
    } else {
      toaster({
        title: translate('required-actions.dialog.documentUploadError'),
        description: `Document Role missing for ${action.type}`,
        variant: 'negative',
      })
    }
  }

  const onSelectDocument = (file: File | null) => {
    setSelectedFile(file)
    if (file) {
      setSubmitCallback(async () => await handleUploadDocument(file))
    } else {
      setSubmitCallback(undefined)
    }
  }
  return selectedFile ? (
    <UploadedFile selectedFile={selectedFile} onSelectDocument={onSelectDocument} />
  ) : (
    <UploadFile onSelectDocument={onSelectDocument} />
  )
}
